import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzTableModule } from 'ng-zorro-antd/table';
import { take } from 'rxjs';
import { Goal, GoalSubData } from '../../../core/goal/goal';
import { GoalService } from '../../../core/goal/goal.service';

@Component({
  selector: 'app-new-edit-goal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,

    NzIconModule,
    NzInputModule,
    NzButtonModule,
    NzTableModule,
    NzPopoverModule,
    NzModalModule,
    NzAlertModule,
    NzFormModule,
  ],
  templateUrl: './new-edit-goal.component.html',
  styleUrl: './new-edit-goal.component.scss',
})
export class NewEditGoalComponent {
  @Input() goalSubData?: GoalSubData;
  @Input() selectedGoal?: Goal;
  @Input() isNewEditGoalVisible = false;

  @Output() isNewEditGoalVisibleChange = new EventEmitter<boolean>();

  @Input() isEditGoal = false;

  validateForm: FormGroup<{
    company: FormControl<string>;
    position: FormControl<string>;
    companyDetails: FormControl<string>;
    jobDescription: FormControl<string>;
  }>;

  constructor(
    private goalService: GoalService,
    private fb: NonNullableFormBuilder
  ) {
    this.validateForm = this.fb.group({
      company: ['', [Validators.required]],
      position: ['', [Validators.required]],
      companyDetails: ['', [Validators.required]],
      jobDescription: ['', [Validators.required]],
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    for (const propName in changes) {
      if (propName === 'goalSubData') {
        const chng = changes[propName];

        if (chng.previousValue !== chng.currentValue) {
          this.validateForm.setValue({
            company: chng.currentValue?.company,
            position: chng.currentValue?.position,
            companyDetails: chng.currentValue?.companyDetails,
            jobDescription: chng.currentValue?.jobDescription,
          });
        }
      }
    }
  }

  submitForm() {
    if (this.validateForm.valid) {
      this.isNewEditGoalVisible = false;
      this.isNewEditGoalVisibleChange.emit(false);

      if (!this.goalSubData) {
        this.goalSubData = {} as any;
      }

      this.goalSubData!.company = this.validateForm.value.company ?? '';
      this.goalSubData!.position = this.validateForm.value.position ?? '';
      this.goalSubData!.companyDetails = this.validateForm.value.companyDetails ?? '';
      this.goalSubData!.jobDescription = this.validateForm.value.jobDescription ?? '';

      if (this.isEditGoal) {
        this.selectedGoal!.subData = this.goalSubData!;

        this.goalService
          .updateGoal$(this.selectedGoal!)
          .pipe(take(1))
          .subscribe(() => {
            this.goalSubData = {
              company: '',
              position: '',
              companyDetails: '',
              jobDescription: '',
            };
          });
      } else {
        this.goalService
          .addGoal$(this.goalSubData!)
          .pipe(take(1))
          .subscribe(() => {
            this.goalSubData = {
              company: '',
              position: '',
              companyDetails: '',
              jobDescription: '',
            };
          });
      }
    } else {
      Object.values(this.validateForm.controls).forEach((control) => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
    }
  }

  onIsNewEditGoalVisibleChange(event: boolean) {
    this.isNewEditGoalVisibleChange.emit(event);
  }

  onCancelGoalClick() {
    this.isNewEditGoalVisible = false;

    this.isNewEditGoalVisibleChange.emit(false);
  }
}
